import { useId, useState, useEffect, useRef } from 'react';
import ScreenReaderOnly from 'components/accesibility/screen-reader-only';
import styles from './toggle.module.scss';

export default function Toggle({
	options,
	onChange,
	initialActiveOptionIndex,
	label = 'Toggle',
	small = false,
}: ToggleProps) {
	const [activeOptionIdx, setActiveOptionIdx] = useState(
		initialActiveOptionIndex ?? 0,
	);
	const [isReady, setIsReady] = useState(false);
	const [canAnimate, setCanAnimate] = useState(true);
	const pillRef = useRef<HTMLDivElement>(null);
	const optionsRef = useRef<Map<number, HTMLDivElement | null>>(new Map());
	const nameAttr = useId();
	const pillTransitionDuration = Math.min(300, options.length * 100);

	// Disable transition animations on window resize.
	useEffect(() => {
		let timeoutId: number = null;

		const handleResize = () => {
			setCanAnimate(false);

			if (timeoutId) {
				window.clearTimeout(timeoutId);
			}

			timeoutId = window.setTimeout(() => {
				setCanAnimate(true);
				timeoutId = null;
			}, 50);
		};

		window.addEventListener('resize', handleResize);

		return () => {
			window.removeEventListener('resize', handleResize);
		};
	}, []);

	// Resize and position the pill to match the active option.
	useEffect(() => {
		const resizeAndReposition: ResizeObserverCallback = (
			entries: ResizeObserverEntry[],
		) => {
			const activeOptionEntry = entries[activeOptionIdx];
			if (!pillRef.current || !activeOptionEntry) return;

			const optionEl = activeOptionEntry.target;
			// Should never be the case but TS doesn't know that.
			if (!(optionEl instanceof HTMLElement)) return;
			const rect = optionEl.getBoundingClientRect();

			const parentEl = optionEl.offsetParent;
			if (!parentEl) return;
			const parentRect = parentEl.getBoundingClientRect();

			pillRef.current.style.top = `${rect.top - parentRect.top - parentEl.scrollTop}px`;
			pillRef.current.style.left = `${rect.left - parentRect.left - parentEl.scrollLeft}px`;
			pillRef.current.style.width = `${rect.width}px`;
			pillRef.current.style.height = `${rect.height}px`;
		};

		const observer = new ResizeObserver(resizeAndReposition);

		for (const el of optionsRef.current.values()) {
			if (!el) continue;
			observer.observe(el);
		}

		return () => {
			observer.disconnect();
		};
	}, [activeOptionIdx, options]);

	const handleChange = (newIndex: number) => {
		// Change ready state on first user interaction to enable the pill.
		// Waiting for hydration can sometimes be too early because fonts
		// may not have loaded yet and we want to do our best to ensure that
		// any element size changes could occur during loading have completed
		// before switching in the pill element.
		if (!isReady) {
			setIsReady(true);
		}

		setActiveOptionIdx(newIndex);
		onChange(options[newIndex].value);
	};

	return (
		<div
			className={[
				styles.container,
				isReady ? styles.ready : '',
				small ? styles.small : '',
			]
				.filter(Boolean)
				.join(' ')}
			style={
				{
					'--pill-transition-duration': `${pillTransitionDuration}ms`,
				} as React.CSSProperties
			}
			data-testid="toggle-container"
		>
			<fieldset className={styles.toggle} data-testid="toggle-fieldset">
				<ScreenReaderOnly
					Element="legend"
					text={label}
					data-testid="toggle-fieldset-legend"
				/>
				{options.map(({ value, label }, i) => {
					const id = `${nameAttr}-${i}`;
					const isActiveOption = activeOptionIdx === i;

					return (
						<div
							key={label}
							className={[
								styles.option,
								isActiveOption ? styles.active : undefined,
							].join(' ')}
							ref={(el: HTMLDivElement | null) => {
								optionsRef.current.set(i, el);
							}}
						>
							<input
								type="radio"
								id={id}
								name={nameAttr}
								value={value}
								checked={isActiveOption}
								onChange={() => handleChange(i)}
								className={styles.radioInput}
							/>
							<label htmlFor={id} className={styles.label}>
								{label}
							</label>
						</div>
					);
				})}
			</fieldset>
			<div
				ref={pillRef}
				className={[styles.pill, canAnimate ? styles.animate : null].join(' ')}
				aria-hidden="true"
				data-testid="toggle-pill"
			>
				{options[activeOptionIdx].label}
			</div>
		</div>
	);
}

export interface ToggleProps {
	options: {
		value: string | number;
		label: string;
	}[];
	onChange: (value: string | number) => void;
	label?: string;
	initialActiveOptionIndex?: number;
	small?: boolean;
}
